import dayjs from "dayjs";

import { shortMonthAndDay, shortMonthAndYear } from "@similarweb/pro-logic-components";

import { GrowthOverviewGranularity } from "../const";
import type { GrowthOverviewPeriodMetric } from "../growthOverviewApiTypes";
import type { IGrowthOverviewPeriodCell } from "../types";

import { getQuarterNumber, parseGrowthOverviewDate } from "./growthOverviewDateUtils";

type TPeriodFormat = {
    key: (parsed: dayjs.Dayjs) => string;
    label: (parsed: dayjs.Dayjs) => string;
};

const PERIOD_FORMAT: Record<GrowthOverviewGranularity, TPeriodFormat> = {
    [GrowthOverviewGranularity.Weekly]: {
        key: (parsed) => parsed.format("YYYY-MM-DD"),
        label: (parsed) => parsed.format(shortMonthAndDay),
    },
    [GrowthOverviewGranularity.Monthly]: {
        key: (parsed) => parsed.format("YYYY-MM"),
        label: (parsed) => parsed.format(shortMonthAndYear),
    },
    [GrowthOverviewGranularity.Quarterly]: {
        key: (parsed) => `${parsed.year()}-Q${getQuarterNumber(parsed)}`,
        label: (parsed) => `Q${getQuarterNumber(parsed)} ${parsed.year()}`,
    },
};

const isValidPeriodCell = (period: IGrowthOverviewPeriodCell): boolean =>
    Boolean(period.key) && !period.key.includes("NaN") && parseGrowthOverviewDate(period.date) != null;

const buildPeriodCellsFromSeries = (
    series: GrowthOverviewPeriodMetric[],
    granularity: GrowthOverviewGranularity,
): IGrowthOverviewPeriodCell[] => {
    const format = PERIOD_FORMAT[granularity];
    const sorted = series
        .filter((point) => parseGrowthOverviewDate(point.date) != null)
        .toSorted((left, right) => dayjs(left.date).valueOf() - dayjs(right.date).valueOf());

    return sorted
        .map((point) => {
            const parsed = parseGrowthOverviewDate(point.date)!;

            return {
                key: format.key(parsed),
                label: format.label(parsed),
                date: point.date,
                value: point.value ?? null,
                pop: point.pop ?? null,
                isIncomplete: point.isIncomplete === true,
            };
        })
        .filter(isValidPeriodCell);
};

export const buildPeriodsFromSeries = (
    series: GrowthOverviewPeriodMetric[],
    granularity: GrowthOverviewGranularity,
): IGrowthOverviewPeriodCell[] => buildPeriodCellsFromSeries(series, granularity);

export const isValidGrowthOverviewPeriod = isValidPeriodCell;
